/**
 * Music Stream — two-element crossfade via HTMLAudioElement + GainNode.
 *
 * Web Audio API (W3C) verified via Context7 MCP:
 * - `createMediaElementSource(el)` creates exactly ONE MediaElementAudioSourceNode per
 *   HTMLAudioElement; attempting to create a second throws InvalidStateError.
 * - `linearRampToValueAtTime(value, endTime)` — endTime is in absolute AudioContext
 *   seconds; the implicit start value is whatever the AudioParam currently holds.
 *
 * Signal chain per slot: mediaSrc → trackGain → musicGain (user-volume node).
 * Crossfades ramp `trackGain.gain` only — never `musicGain.gain`.
 */

import type { MusicTrackId } from './types';

// ── Public interfaces ─────────────────────────────────────────────────────────

export interface MusicStream {
  /**
   * Play the given music track. If no track is currently playing, fades in.
   * If the same track is already playing, this is a NO-OP.
   * If a different track is playing, crossfades.
   * Default: fadeMs = 1000, loop = true.
   */
  play(id: MusicTrackId, opts?: { fadeMs?: number; loop?: boolean }): void;

  /**
   * Fade out the active track and pause it.
   * Default: fadeMs = 500. NO-OP if nothing is playing.
   */
  stop(opts?: { fadeMs?: number }): void;

  /**
   * Returns the currently-playing (or fading-in) track id, or null if stopped.
   */
  getCurrentTrack(): MusicTrackId | null;

  /**
   * Pauses both elements, disconnects all nodes, clears refs. Idempotent.
   */
  dispose(): void;
}

export interface MusicStreamDeps {
  readonly audioContext: AudioContext;
  /** The AudioManager's music volume node; crossfades must NOT ramp its gain. */
  readonly musicGain: GainNode;
  readonly resolveUrl: (id: MusicTrackId) => string;
  /**
   * Optional injection point for tests.
   * Defaults to `(el) => audioContext.createMediaElementSource(el)`.
   */
  readonly createMediaElementSource?: (el: HTMLAudioElement) => MediaElementAudioSourceNode;
  /**
   * Optional injection point for tests to substitute a fake HTMLAudioElement.
   * Defaults to `() => new Audio()`.
   */
  readonly createAudioElement?: () => HTMLAudioElement;
}

// ── Internal types ────────────────────────────────────────────────────────────

interface Slot {
  element: HTMLAudioElement;
  mediaSrc: MediaElementAudioSourceNode;
  trackGain: GainNode;
}

// ── Factory ───────────────────────────────────────────────────────────────────

export function createMusicStream(deps: MusicStreamDeps): MusicStream {
  const {
    audioContext,
    musicGain,
    resolveUrl,
    createMediaElementSource = (el) => audioContext.createMediaElementSource(el),
    createAudioElement = () => new Audio(),
  } = deps;

  // Build two slots up-front. Each slot gets a dedicated trackGain node.
  // Both trackGain nodes start at gain 0 (silent).
  function makeSlot(): Slot {
    const element = createAudioElement();
    const mediaSrc = createMediaElementSource(element);
    const trackGain = audioContext.createGain();
    trackGain.gain.value = 0;
    mediaSrc.connect(trackGain);
    trackGain.connect(musicGain);
    return { element, mediaSrc, trackGain };
  }

  const slots: [Slot, Slot] = [makeSlot(), makeSlot()];
  // activeIndex tracks which slot is currently active (playing or last played).
  // Initialise to 1 so the FIRST play() picks incomingIndex = 0 (slot A).
  // After each play(), activeIndex is updated to the incomingIndex.
  // Invariant: slots[activeIndex] is always the "outgoing" candidate on next play.
  let activeIndex: 0 | 1 = 1;
  let currentTrack: MusicTrackId | null = null;
  let disposed = false;

  function play(id: MusicTrackId, opts?: { fadeMs?: number; loop?: boolean }): void {
    if (disposed) return;

    // NO-OP if the same track is already active
    if (currentTrack === id) return;

    const fadeMs = opts?.fadeMs ?? 1000;
    const loop = opts?.loop ?? true;
    const fadeSec = fadeMs / 1000;
    const endTime = audioContext.currentTime + fadeSec;

    const outgoingIndex = activeIndex;
    const incomingIndex: 0 | 1 = activeIndex === 0 ? 1 : 0;

    // Fade out the outgoing slot only if something is currently playing
    if (currentTrack !== null) {
      slots[outgoingIndex].trackGain.gain.linearRampToValueAtTime(0, endTime);
    }

    // Configure and start the incoming slot
    const incoming = slots[incomingIndex];
    incoming.element.src = resolveUrl(id);
    incoming.element.loop = loop;
    incoming.element.load();
    // audio.play() returns a Promise that may reject on autoplay-policy blocks;
    // catch + warn but do NOT throw (AudioManager handles context resumption).
    incoming.element
      .play()
      ?.catch?.((err: unknown) =>
        console.warn('[music-stream] play() rejected (autoplay policy?):', err),
      );

    // Ramp the incoming slot from 0 → 1
    incoming.trackGain.gain.linearRampToValueAtTime(1, endTime);

    // Update state: incoming slot is now active; flip activeIndex
    currentTrack = id;
    activeIndex = incomingIndex;
  }

  function stop(opts?: { fadeMs?: number }): void {
    if (disposed) return;
    if (currentTrack === null) return;

    const fadeMs = opts?.fadeMs ?? 500;
    const fadeSec = fadeMs / 1000;
    const endTime = audioContext.currentTime + fadeSec;

    const active = slots[activeIndex];
    active.trackGain.gain.linearRampToValueAtTime(0, endTime);

    // Use setTimeout so tests can advance fake timers to trigger the pause
    setTimeout(() => {
      active.element.pause();
    }, fadeMs);

    currentTrack = null;
  }

  function getCurrentTrack(): MusicTrackId | null {
    return currentTrack;
  }

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    for (const slot of slots) {
      slot.element.pause();
      slot.mediaSrc.disconnect();
      slot.trackGain.disconnect();
    }

    currentTrack = null;
  }

  return { play, stop, getCurrentTrack, dispose };
}
